import React from 'react';
import { HostMetricsBar, ProcessStateIndicator } from './shared';

interface Device {
  id: number;
  name: string;
  status: 0 | 1;
  socketConnected?: boolean;
  deviceTypes?: {
    id: number;
    name: string;
    icon?: string;
  };
  settings?: Record<string, any>;
}

interface HostMetrics {
  cpuPercent: number;
  ramPercent: number;
  ramUsedGB?: number;
  ramTotalGB?: number;
  cpuTemp?: number;
  gpuTemp?: number;
  diskPercent?: number;
  diskUsedGB?: number;
  diskTotalGB?: number;
  powerWatts?: number;
}

interface GroupHeaderProps {
  /** Group name to display */
  groupName: string;
  /** Host device name (shown as subtitle) */
  hostName: string;
  /** Host device for status checking */
  hostDevice: Device;
  /** Host CPU/RAM metrics */
  hostMetrics: HostMetrics;
  /** Count of active devices in group */
  activeDevicesCount: number;
  /** Theme color for this group */
  themeColor: string;
  /** Icon for this group */
  icon: string;
  /** Optional process state to display */
  processState?: string;
}

/**
 * Unified group header component
 * Displays group name, host status, CPU/RAM metrics, and action buttons
 */
const GroupHeader: React.FC<GroupHeaderProps> = ({
  groupName,
  hostName,
  hostDevice,
  hostMetrics,
  activeDevicesCount,
  themeColor,
  icon,
  processState,
}) => {
  const isHostOnline = hostDevice.socketConnected === true;

  return (
    <div
      style={{
        background: `linear-gradient(135deg, ${themeColor}15 0%, ${themeColor}05 100%)`,
        border: `1px solid ${themeColor}40`,
        borderLeft: `4px solid ${themeColor}`,
        borderRadius: '8px',
        padding: '1rem 1.25rem',
        marginBottom: '1.5rem',
        boxShadow: `0 4px 20px ${themeColor}20, inset 0 1px 0 ${themeColor}30`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated top scan line */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: `linear-gradient(90deg, transparent, ${themeColor}, transparent)`,
          animation: 'scan 3s linear infinite',
        }}
      />

      {/* Row 1: Group info + Active Devices Count */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'relative',
        zIndex: 1,
        gap: '1rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {/* Device Icon */}
          <div
            style={{
              fontSize: '2rem',
              background: `${themeColor}20`,
              padding: '0.5rem',
              borderRadius: '8px',
              border: `1px solid ${themeColor}40`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {icon}
          </div>

          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              {/* Status Indicator */}
              <div
                style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: isHostOnline ? '#00ff88' : '#ff3366',
                  boxShadow: isHostOnline
                    ? '0 0 12px #00ff8888, 0 0 24px #00ff8844'
                    : '0 0 12px #ff336688, 0 0 24px #ff336644',
                  animation: 'pulse 2s ease-in-out infinite',
                  flexShrink: 0,
                }}
              />
              <h3 style={{
                margin: 0,
                color: themeColor,
                fontWeight: 700,
                fontSize: '1.3rem',
                textShadow: `0 0 20px ${themeColor}88`,
                letterSpacing: '0.5px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {groupName}
              </h3>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.25rem' }}>
              <span style={{
                fontSize: '0.75rem',
                color: `${themeColor}cc`,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {hostName}
              </span>
            </div>
          </div>
        </div>

        {/* Active Devices Count - always in first row */}
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{
            fontSize: '0.7rem',
            color: '#6c757d',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            marginBottom: '4px',
            whiteSpace: 'nowrap',
          }}>
            ACTIVE DEVICES
          </div>
          <div style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: themeColor,
            fontFamily: 'monospace',
          }}>
            {activeDevicesCount.toString().padStart(2, '0')}
          </div>
        </div>
      </div>

      {/* Row 2: Metrics + Process State */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        marginTop: '0.75rem',
        flexWrap: 'wrap',
      }}>
        <HostMetricsBar
          cpuPercent={hostMetrics.cpuPercent}
          ramPercent={hostMetrics.ramPercent}
          ramUsedGB={hostMetrics.ramUsedGB}
          ramTotalGB={hostMetrics.ramTotalGB}
          cpuTemp={hostMetrics.cpuTemp}
          gpuTemp={hostMetrics.gpuTemp}
          diskPercent={hostMetrics.diskPercent}
          diskUsedGB={hostMetrics.diskUsedGB}
          diskTotalGB={hostMetrics.diskTotalGB}
          powerWatts={hostMetrics.powerWatts}
        />

        {/* Process State (if provided) */}
        {processState && (
          <ProcessStateIndicator state={processState} />
        )}
      </div>
    </div>
  );
};

export default GroupHeader;
export type { GroupHeaderProps, HostMetrics };
